import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';
import { useAuth } from '../context/AuthContext';
import { useDebounce } from '../hooks/useDebounce';
import { QK } from '../queryKeys';
import ExportButton from '../components/common/ExportButton';

const CATEGORIES = ['impurity_standard', 'reference_standard', 'metabolite', 'degradation_product', 'synthesis_intermediate', 'custom'];
const PHARMACOPEIAS = ['USP', 'EP', 'BP', 'JP', 'ICH', 'In-house', 'N/A'];
const REG_CLASSES = ['ICH Q3A', 'ICH Q3B', 'ICH Q3C', 'ICH Q3D', 'N/A'];
const STOCK_STATUSES = ['in_stock', 'limited_stock', 'out_of_stock', 'made_to_order'];
const catLbl = c => c.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
const stockColor = { in_stock: 'success', limited_stock: 'warning', out_of_stock: 'danger', made_to_order: 'info' };

const defaultForm = {
  name: '', category: 'impurity_standard', cas_number: '', molecular_formula: '', molecular_weight: '',
  pharmacopeia: '', regulatory_class: '', purity: '', pack_sizes: '', price: '', cost_price: '', stock_status: 'in_stock',
  description: '', synonyms: '', image: '', hsn_code: '', currency: 'INR', storage: '', lead_time: '',
  show_on_website: false, parent_product: '', min_stock_alert: '', stock_quantity: '',
};

// Simple CSV → array of objects parser (no external deps)
function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
  return lines.slice(1).map(line => {
    const vals = [];
    let cur = '', inQ = false;
    for (const ch of line) {
      if (ch === '"') { inQ = !inQ; }
      else if (ch === ',' && !inQ) { vals.push(cur); cur = ''; }
      else cur += ch;
    }
    vals.push(cur);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (vals[i] || '').trim(); });
    return obj;
  });
}

const CSV_TEMPLATE = `name,catalog_number,cas_number,molecular_formula,molecular_weight,category,pharmacopeia,purity,pack_sizes,price,currency,storage,lead_time,stock_status,hsn_code,synonyms,image_file_name,show_on_website,parent_product,description
tert-butyl 2-(trifluoromethylsulfonyloxy)spiro[5.6-dihydropyrrolo[1,2-b]pyrazole-4,7-pyridine],APF001,2640436-81-1,C19H20F3N3O3S,411.3966,impurity_standard,In-house,>98.0%,"5mg, 25mg, 100mg",1000,INR,"2-8°C for long term","1-2 weeks",in_stock,29349900,"tert-butyl 2-(trifluoromethylsulfonyloxy)spiro[5.6-dihydropyrrolo[1,2-b]pyrazole-4,7-pyridine]",APF001.PNG,1,1,Pharmaceutical impurity standard
tert-butyl 2-(trifluoromethylsulfonyloxy)spiro[5.6-dihydropyrrolo[1,2-b]pyrazole-4,7-pyridine],APRO1,2640436-81-1,C19H20F3N3O3S,411.3966,impurity_standard,In-house,>=98.0%,"5mg, 25mg, 100mg",1,INR,"2-8°C for long term","1-2 weeks",in_stock,29349900,"tert-butyl 2-(trifluoromethylsulfonyloxy)spiro[5.6-dihydropyrrolo[1,2-b]pyrazole-4,7-pyridine]",APRO1.PNG,1,,Pharmaceutical impurity standard`;

const API_BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api$/, '') : '';

// Old product images stored as uppercase filenames like PL0719.PNG → served from /products/
// New uploaded images stored as hex filenames with lowercase ext → served from backend /uploads/
function getCatalogImageUrl(image) {
  if (!image) return null;
  if (/\.[A-Z]{2,4}$/.test(image)) return `/products/${image}`;
  return `${API_BASE}/uploads/catalog-images/${image}`;
}

export default function Catalog() {
  const { isAdmin, isManager } = useAuth();
  const canWrite = isAdmin || isManager;
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState({ open: false, mode: 'add', item: null });
  const [viewModal, setViewModal] = useState({ open: false, item: null });
  const [form, setForm] = useState(defaultForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState('');
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [profitModal, setProfitModal] = useState(false);
  const [profitData, setProfitData] = useState([]);
  const [profitLoading, setProfitLoading] = useState(false);
  const [imgBulkModal, setImgBulkModal] = useState(false);
  const [imgBulkFiles, setImgBulkFiles] = useState(null);
  const [imgBulkUploading, setImgBulkUploading] = useState(false);
  const [imgBulkResult, setImgBulkResult] = useState(null);
  const [imgBulkError, setImgBulkError] = useState('');

  // PubChem lookup state
  const [pubchemLoading, setPubchemLoading] = useState(false);
  const [pubchemResult, setPubchemResult] = useState(null); // { found, iupac_name, molecular_formula, molecular_weight, synonyms } | null
  const [pubchemApplied, setPubchemApplied] = useState(false);

  const debouncedSearch = useDebounce(search, 300);

  const { data: catalogData, isLoading: loading } = useQuery({
    queryKey: QK.catalog({ page, search: debouncedSearch, category: categoryFilter, stock_status: stockFilter }),
    queryFn: () => api.get('/catalog', { params: { page, limit: 15, search: debouncedSearch || undefined, category: categoryFilter || undefined, stock_status: stockFilter || undefined } }).then(r => r.data),
    keepPreviousData: true,
  });

  const data = catalogData ?? { data: [], total: 0, page: 1, limit: 15 };

  const resetPubchem = () => { setPubchemResult(null); setPubchemLoading(false); setPubchemApplied(false); };

  const openAdd = () => { setForm(defaultForm); setImageFile(null); setImagePreview(null); setError(''); resetPubchem(); setModal({ open: true, mode: 'add', item: null }); };
  const openEdit = (item) => {
    setForm({ 
      ...item, 
      cas_number: item.cas_number || '',
      pack_sizes: Array.isArray(item.pack_sizes) ? item.pack_sizes.join(', ') : (item.pack_sizes || ''), 
      show_on_website: !!item.show_on_website, 
      parent_product: item.parent_product || '', 
      synonyms: item.synonyms || '' 
    });
    setImageFile(null);
    setImagePreview(item.image ? getCatalogImageUrl(item.image) : null);
    setError('');
    resetPubchem();
    setModal({ open: true, mode: 'edit', item });
  };

  const handlePubchemLookup = async (casValue) => {
    const cas = (casValue || form.cas_number || '').toString().trim();
    if (!cas) return;
    setPubchemLoading(true);
    setPubchemResult(null);
    setPubchemApplied(false);
    try {
      const { data: res } = await api.get('/catalog/pubchem', { params: { cas } });
      setPubchemResult(res);
    } catch (err) {
      setPubchemResult({ found: false });
    } finally {
      setPubchemLoading(false);
    }
  };

  const handlePubchemApply = () => {
    if (!pubchemResult || !pubchemResult.found) return;
    setForm(prev => ({
      ...prev,
      molecular_formula: pubchemResult.molecular_formula || prev.molecular_formula,
      molecular_weight: pubchemResult.molecular_weight || prev.molecular_weight,
      synonyms: pubchemResult.synonyms
        ? (prev.synonyms ? prev.synonyms + ', ' + pubchemResult.synonyms : pubchemResult.synonyms)
        : prev.synonyms,
    }));
    setPubchemApplied(true);
  };
  const openView = (item) => setViewModal({ open: true, item });

  const handleSave = async () => {
    if (!form.name || !form.category) { setError('Name and category are required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, molecular_weight: form.molecular_weight ? Number(form.molecular_weight) : null, price: form.price ? Number(form.price) : null };
      let savedId;
      if (modal.mode === 'add') {
        const res = await api.post('/catalog', payload);
        savedId = res.data.data.id;
      } else {
        await api.put(`/catalog/${modal.item.id}`, payload);
        savedId = modal.item.id;
      }
      if (imageFile) {
        const fd = new FormData();
        fd.append('image', imageFile);
        await api.post(`/catalog/${savedId}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setModal({ open: false }); setImageFile(null); setImagePreview(null);
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
    } catch (err) { setError(err.response?.data?.message || 'Failed to save catalog item.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    await api.delete(`/catalog/${deleteConfirm.id}`);
    setDeleteConfirm(null);
    queryClient.invalidateQueries({ queryKey: ['catalog'] });
  };

  const handleUnfreeze = async (id) => {
    try {
      await api.patch(`/catalog/${id}/unfreeze`);
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
      setModal({ open: false });
    }
    catch (err) { setError(err.response?.data?.message || 'Failed to unfreeze.'); }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) { setBulkError('Please select a CSV or JSON file.'); return; }
    setBulkUploading(true); setBulkError(''); setBulkResult(null);
    try {
      const text = await bulkFile.text();
      let items;
      if (bulkFile.name.endsWith('.json')) {
        items = JSON.parse(text);
      } else {
        items = parseCSV(text);
      }
      if (!Array.isArray(items) || items.length === 0) { setBulkError('No valid items found in file.'); setBulkUploading(false); return; }
      // Map image_file_name → image for compatibility
      const mapped = items.map(it => ({ ...it, image: it.image || it.image_file_name || '', show_on_website: it.show_on_website === '1' || it.show_on_website === true || it.show_on_website === 'true' ? 1 : 0 }));
      const { data: res } = await api.post('/catalog/bulk', mapped);
      setBulkResult(res);
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
    } catch (err) {
      setBulkError(err.response?.data?.message || err.message || 'Upload failed.');
    } finally { setBulkUploading(false); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'catalog_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkImageUpload = async () => {
    if (!imgBulkFiles || imgBulkFiles.length === 0) { setImgBulkError('Please select at least one image file.'); return; }
    setImgBulkUploading(true); setImgBulkError(''); setImgBulkResult(null);
    try {
      const fd = new FormData();
      Array.from(imgBulkFiles).forEach(f => fd.append('images', f));
      const { data: res } = await api.post('/catalog/images/bulk', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImgBulkResult(res);
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
    } catch (err) {
      setImgBulkError(err.response?.data?.message || 'Upload failed.');
    } finally {
      setImgBulkUploading(false);
    }
  };

  const openProfitability = async () => {
    setProfitLoading(true); setProfitModal(true);
    try {
      const { data: res } = await api.get('/catalog/profitability');
      setProfitData(Array.isArray(res) ? res : []);
    } catch { setProfitData([]); }
    finally { setProfitLoading(false); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const packSizes = (item) => {
    if (!item.pack_sizes) return '—';
    if (Array.isArray(item.pack_sizes)) return item.pack_sizes.join(', ');
    return item.pack_sizes;
  };

  return (
    <div className="page" style={{ paddingTop: 24 }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Catalog</h1>
          <p className="page-subtitle">Impurity standards, reference standards and custom synthesis catalog</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isAdmin && <ExportButton endpoint="/export/catalog" params={{ category: categoryFilter, search }} label="Export" filename="catalog" />}
          <button className="btn btn-secondary" onClick={openProfitability}>
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
            Profitability
          </button>
          {canWrite && (
            <>
              <button className="btn btn-secondary" onClick={() => { setBulkFile(null); setBulkResult(null); setBulkError(''); setBulkModal(true); }}>
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                Bulk Upload
              </button>
              <button className="btn btn-secondary" onClick={() => { setImgBulkFiles(null); setImgBulkResult(null); setImgBulkError(''); setImgBulkModal(true); }}>
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                Upload Images
              </button>
              <button className="btn btn-primary" onClick={openAdd}>
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                Add Item
              </button>
            </>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '14px 16px' }}>
          <div className="search-bar">
            <div className="search-input-wrap">
              <span className="search-icon"><svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg></span>
              <input className="form-control" placeholder="Search name, CAS number, formula…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="form-control form-select" style={{ width: 200 }} value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}>
              <option value="">All Categories</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{catLbl(c)}</option>)}
            </select>
            <select className="form-control form-select" style={{ width: 160 }} value={stockFilter} onChange={e => { setStockFilter(e.target.value); setPage(1); }}>
              <option value="">All Stock</option>
              {STOCK_STATUSES.map(s => <option key={s} value={s}>{catLbl(s)}</option>)}
            </select>
            {(search || categoryFilter || stockFilter) && <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setCategoryFilter(''); setStockFilter(''); setPage(1); }}>Clear</button>}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead><tr><th style={{ width: 52 }}></th><th>Name</th><th>Category</th><th>CAS Number</th><th>Formula</th><th>Purity</th><th>Stock</th><th>Price</th><th>Actions</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={9}><div className="loading-overlay"><div className="spinner" /></div></td></tr>}
              {!loading && data.data.length === 0 && <tr><td colSpan={9}><div className="empty-state"><p>No catalog items found.</p></div></td></tr>}
              {!loading && data.data.map(item => (
                <tr key={item.id}>
                  <td style={{ padding: '6px 8px' }}>
                    {item.image
                      ? <img src={getCatalogImageUrl(item.image)} alt={item.name} style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 4, border: '1px solid #e2e8f0', background: '#f8fafc' }} onError={e => { e.target.style.display = 'none'; }} />
                      : <div style={{ width: 40, height: 40, borderRadius: 4, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><svg width="16" height="16" fill="none" stroke="#1d4ed8" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg></div>
                    }
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: item.is_active === 0 ? 'var(--text-muted)' : undefined }}>
                      {item.name}
                      {item.is_active === 0 && <span className="badge badge-neutral" style={{ fontSize: '0.65rem', marginLeft: 6 }}>Frozen</span>}
                    </div>
                  </td>
                  <td><span className="badge badge-neutral" style={{ fontSize: '0.72rem' }}>{catLbl(item.category)}</span></td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{item.cas_number || '—'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{item.molecular_formula || '—'}</td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: '0.83rem' }}>{item.purity || '—'}</td>
                  <td>
                    <span className={`badge badge-${stockColor[item.stock_status] || 'neutral'}`} style={{ fontSize: '0.72rem' }}>{catLbl(item.stock_status)}</span>
                    {item.min_stock_alert != null && item.stock_quantity != null && Number(item.stock_quantity) <= Number(item.min_stock_alert) && (
                      <span title={`Stock quantity (${item.stock_quantity}) is at or below alert threshold (${item.min_stock_alert})`} style={{ marginLeft: 5, color: '#ef4444', display: 'inline-flex', verticalAlign: 'middle', cursor: 'default' }}>
                        <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2L1 21h22L12 2zm0 3.5L20.5 19h-17L12 5.5zM11 10v4h2v-4h-2zm0 6v2h2v-2h-2z" /></svg>
                      </span>
                    )}
                  </td>
                  <td style={{ fontWeight: 600, fontSize: '0.83rem' }}>{item.price ? `${item.currency === 'INR' ? '₹' : '$'}${Number(item.price).toLocaleString()}` : '—'}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-ghost btn-icon" data-tooltip="View" onClick={() => openView(item)}>
                        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                      </button>
                      {canWrite && (
                        <button className="btn btn-ghost btn-icon" data-tooltip="Edit" onClick={() => openEdit(item)}>
                          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                        </button>
                      )}
                      {isAdmin && item.is_active !== 0 && (
                        <button className="btn btn-ghost btn-icon" data-tooltip="Freeze" style={{ color: 'var(--error)' }} onClick={() => setDeleteConfirm(item)}>
                          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={data.page} total={data.total} limit={data.limit} onPageChange={setPage} />
      </div>

      {/* Add/Edit Modal */}
      <Modal open={modal.open} onClose={() => setModal({ open: false })} title={modal.mode === 'add' ? 'Add Catalog Item' : 'Edit Catalog Item'} size="lg"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setModal({ open: false })}>Cancel</button>
          {modal.item?.is_active === 0
            ? <button className="btn btn-primary" style={{ background: '#dc2626' }} onClick={() => handleUnfreeze(modal.item.id)}>Unfreeze</button>
            : <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Item'}</button>
          }
        </>}
      >
        {error && <div className="alert alert-error">{error}</div>}
        {modal.item?.is_active === 0 && (
          <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
            <div style={{ fontWeight: 700, color: '#dc2626', fontSize: '0.875rem' }}>⛔ Record Frozen</div>
            <div style={{ color: '#991b1b', fontSize: '0.8rem', marginTop: 2 }}>This record is frozen and cannot be edited. Unfreeze to make changes.</div>
          </div>
        )}
        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label className="form-label">Name *</label>
            <input className="form-control" value={form.name} onChange={e => f('name', e.target.value)} placeholder="Metformin Related Substance A" disabled={modal.item?.is_active === 0} />
          </div>
          <div className="form-group">
            <label className="form-label">Catalog #</label>
            <input className="form-control" value={form.catalog_number || ''} onChange={e => f('catalog_number', e.target.value)} placeholder="e.g. PL0719" disabled={modal.item?.is_active === 0} />
          </div>
          <div className="form-group">
            <label className="form-label">Category *</label>
            <select className="form-control form-select" value={form.category} onChange={e => f('category', e.target.value)} disabled={modal.item?.is_active === 0}>
              {CATEGORIES.map(c => <option key={c} value={c}>{catLbl(c)}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">CAS Number</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                className="form-control"
                value={form.cas_number}
                onChange={e => { f('cas_number', e.target.value); setPubchemResult(null); setPubchemApplied(false); }}
                onBlur={e => { if (e.target.value && e.target.value.trim()) handlePubchemLookup(e.target.value.trim()); }}
                placeholder="1817-57-8"
                style={{ fontFamily: 'monospace', flex: 1 }}
                disabled={modal.item?.is_active === 0}
              />
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => handlePubchemLookup()}
                disabled={!(form.cas_number && form.cas_number.trim()) || pubchemLoading || modal.item?.is_active === 0}
                title="Look up on PubChem"
                style={{
                  background: '#0891b2', color: '#fff', border: 'none',
                  borderRadius: 6, padding: '0 10px', height: 34, flexShrink: 0,
                  display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem',
                  fontWeight: 600, cursor: 'pointer', opacity: (!(form.cas_number && form.cas_number.trim()) || modal.item?.is_active === 0) ? 0.5 : 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {pubchemLoading ? (
                  <span style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                ) : (
                  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                )}
                Lookup
              </button>
            </div>
            {/* PubChem result banner */}
            {pubchemResult && !pubchemLoading && (
              pubchemResult.found ? (
                pubchemApplied ? (
                  <div style={{ marginTop: 6, padding: '6px 10px', background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 6, fontSize: '0.77rem', color: '#065f46', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
                    Applied: {pubchemResult.molecular_formula} — {pubchemResult.iupac_name}
                  </div>
                ) : (
                  <div style={{ marginTop: 6, padding: '8px 10px', background: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: 6, fontSize: '0.77rem', color: '#0e7490' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600 }}>Found on PubChem:</span>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{pubchemResult.molecular_formula}{pubchemResult.molecular_weight ? ` · ${pubchemResult.molecular_weight} g/mol` : ''}</span>
                      <button
                        type="button"
                        onClick={handlePubchemApply}
                        style={{ background: '#0891b2', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 10px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                      >
                        Apply
                      </button>
                    </div>
                    {pubchemResult.iupac_name && (
                      <div style={{ marginTop: 3, fontSize: '0.73rem', color: '#0c4a6e', wordBreak: 'break-word' }}>
                        {pubchemResult.iupac_name}
                      </div>
                    )}
                  </div>
                )
              ) : (
                <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic' }}>
                  Not found on PubChem
                </div>
              )
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Molecular Formula</label>
            <input className="form-control" value={form.molecular_formula} onChange={e => f('molecular_formula', e.target.value)} placeholder="C₄H₁₁N₅" style={{ fontFamily: 'monospace' }} disabled={modal.item?.is_active === 0} />
            {pubchemApplied && pubchemResult?.molecular_formula && (
              <div style={{ fontSize: '0.7rem', color: '#0891b2', marginTop: 3 }}>via PubChem</div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Mol. Weight (g/mol)</label>
            <input className="form-control" type="number" value={form.molecular_weight} onChange={e => f('molecular_weight', e.target.value)} disabled={modal.item?.is_active === 0} />
            {pubchemApplied && pubchemResult?.molecular_weight && (
              <div style={{ fontSize: '0.7rem', color: '#0891b2', marginTop: 3 }}>via PubChem</div>
            )}
          </div>
        </div>
        {/* PubChem credit when fields are auto-filled */}
        {pubchemApplied && (
          <div style={{ marginTop: -8, marginBottom: 8, fontSize: '0.72rem', color: '#6b7280', textAlign: 'right' }}>
            Chemical data auto-filled via{' '}
            <a href={`https://pubchem.ncbi.nlm.nih.gov/compound/${encodeURIComponent(form.cas_number)}`} target="_blank" rel="noopener noreferrer" style={{ color: '#0891b2', textDecoration: 'none' }}>
              PubChem
            </a>
          </div>
        )}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Pharmacopeia</label>
            <select className="form-control form-select" value={form.pharmacopeia} onChange={e => f('pharmacopeia', e.target.value)} disabled={modal.item?.is_active === 0}>
              <option value="">Select</option>
              {PHARMACOPEIAS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Regulatory Class</label>
            <select className="form-control form-select" value={form.regulatory_class} onChange={e => f('regulatory_class', e.target.value)} disabled={modal.item?.is_active === 0}>
              <option value="">Select</option>
              {REG_CLASSES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="form-group"><label className="form-label">Purity</label><input className="form-control" value={form.purity} onChange={e => f('purity', e.target.value)} placeholder="≥98.0%" disabled={modal.item?.is_active === 0} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Pack Sizes (comma-separated)</label><input className="form-control" value={form.pack_sizes} onChange={e => f('pack_sizes', e.target.value)} placeholder="5mg, 10mg, 25mg, 100mg" disabled={modal.item?.is_active === 0} /></div>
          <div className="form-group">
            <label className="form-label">Stock Status</label>
            <select className="form-control form-select" value={form.stock_status} onChange={e => f('stock_status', e.target.value)} disabled={modal.item?.is_active === 0}>
              {STOCK_STATUSES.map(s => <option key={s} value={s}>{catLbl(s)}</option>)}
            </select>
          </div>
          <div className="form-group"><label className="form-label">Base Price (₹)</label><input className="form-control" type="number" value={form.price} onChange={e => f('price', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
          <div className="form-group"><label className="form-label">Cost Price (₹)</label><input className="form-control" type="number" value={form.cost_price ?? ''} onChange={e => f('cost_price', e.target.value)} placeholder="Internal cost" disabled={modal.item?.is_active === 0} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Storage</label><input className="form-control" value={form.storage || ''} onChange={e => f('storage', e.target.value)} placeholder="2-8°C" disabled={modal.item?.is_active === 0} /></div>
          <div className="form-group"><label className="form-label">Lead Time</label><input className="form-control" value={form.lead_time || ''} onChange={e => f('lead_time', e.target.value)} placeholder="1-2 weeks" disabled={modal.item?.is_active === 0} /></div>
          <div className="form-group">
            <label className="form-label">Currency</label>
            <select className="form-control form-select" value={form.currency || 'INR'} onChange={e => f('currency', e.target.value)} disabled={modal.item?.is_active === 0}>
              <option value="INR">INR (₹)</option>
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Stock Quantity</label><input className="form-control" type="number" min="0" value={form.stock_quantity ?? ''} onChange={e => f('stock_quantity', e.target.value)} placeholder="Current stock count" disabled={modal.item?.is_active === 0} /></div>
          <div className="form-group"><label className="form-label">Stock Alert Threshold</label><input className="form-control" type="number" min="0" value={form.min_stock_alert ?? ''} onChange={e => f('min_stock_alert', e.target.value)} placeholder="Alert when stock falls below..." disabled={modal.item?.is_active === 0} /></div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Product Image</label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              {imagePreview && (
                <img src={imagePreview} alt="preview"
                  style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 6, border: '1px solid var(--border)', background: '#f8fafc', flexShrink: 0 }}
                  onError={e => { e.target.style.display = 'none'; }} />
              )}
              <div style={{ flex: 1 }}>
                <input type="file" className="form-control" accept=".png,.jpg,.jpeg,.webp,.gif"
                  disabled={modal.item?.is_active === 0}
                  onChange={e => {
                    const file = e.target.files[0] || null;
                    setImageFile(file);
                    setImagePreview(file ? URL.createObjectURL(file) : (form.image ? getCatalogImageUrl(form.image) : null));
                  }} />
                {form.image && !imageFile && (
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 3 }}>Current: {form.image}</div>
                )}
              </div>
            </div>
          </div>
          <div className="form-group"><label className="form-label">HSN Code</label><input className="form-control" value={form.hsn_code || ''} onChange={e => f('hsn_code', e.target.value)} placeholder="29420090" disabled={modal.item?.is_active === 0} /></div>
        </div>
        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}><label className="form-label">Parent Product</label><input className="form-control" value={form.parent_product || ''} onChange={e => f('parent_product', e.target.value)} placeholder="e.g. Metformin (groups impurities on public catalog)" disabled={modal.item?.is_active === 0} /></div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 4 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: modal.item?.is_active === 0 ? 'default' : 'pointer', userSelect: 'none', fontWeight: 500, fontSize: '0.875rem' }}>
              <input type="checkbox" checked={!!form.show_on_website} onChange={e => f('show_on_website', e.target.checked)} style={{ width: 16, height: 16 }} disabled={modal.item?.is_active === 0} />
              Show on Website
            </label>
          </div>
        </div>
        {/* Quick PubChem fetch button for edit mode items with CAS but missing molecular data */}
        {modal.mode === 'edit' && form.cas_number && (!form.molecular_formula || !form.molecular_weight) && !pubchemApplied && modal.item?.is_active !== 0 && (
          <div style={{ marginBottom: 12, padding: '10px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <svg width="15" height="15" fill="none" stroke="#0891b2" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
            <span style={{ fontSize: '0.82rem', color: '#0c4a6e', flex: 1 }}>
              This item has a CAS number but is missing {!form.molecular_formula && !form.molecular_weight ? 'formula and weight' : !form.molecular_formula ? 'molecular formula' : 'molecular weight'}.
            </span>
            <button
              type="button"
              onClick={() => handlePubchemLookup()}
              disabled={pubchemLoading}
              style={{ background: '#0891b2', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
            >
              {pubchemLoading
                ? <span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                : <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
              }
              Fetch from PubChem
            </button>
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Synonyms / Other Names</label>
          <input className="form-control" value={form.synonyms || ''} onChange={e => f('synonyms', e.target.value)} placeholder="Comma-separated synonyms or alternate names" disabled={modal.item?.is_active === 0} />
          {pubchemApplied && pubchemResult?.synonyms && (
            <div style={{ fontSize: '0.7rem', color: '#0891b2', marginTop: 3 }}>Synonyms appended via PubChem</div>
          )}
        </div>
        <div className="form-group"><label className="form-label">Description</label><textarea className="form-control" rows={2} value={form.description} onChange={e => f('description', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
      </Modal>

      {/* View Modal */}
      <Modal open={viewModal.open} onClose={() => setViewModal({ open: false })} title="Catalog Item Details" size="lg"
        footer={<button className="btn btn-secondary" onClick={() => setViewModal({ open: false })}>Close</button>}
      >
        {viewModal.item && (
          <>
            <div style={{ display: 'flex', gap: 20, marginBottom: 16, alignItems: 'flex-start' }}>
              {viewModal.item.image && (
                <img src={getCatalogImageUrl(viewModal.item.image)} alt={viewModal.item.name}
                  style={{ width: 120, height: 120, objectFit: 'contain', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0 }}
                  onError={e => { e.target.style.display = 'none'; }}
                />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: 4 }}>{viewModal.item.name}</div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 8 }}>{viewModal.item.catalog_number}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span className="badge badge-neutral">{catLbl(viewModal.item.category)}</span>
                  <span className={`badge badge-${stockColor[viewModal.item.stock_status] || 'neutral'}`}>{catLbl(viewModal.item.stock_status)}</span>
                  {viewModal.item.pharmacopeia && <span className="badge badge-primary">{viewModal.item.pharmacopeia}</span>}
                  {viewModal.item.regulatory_class && viewModal.item.regulatory_class !== 'N/A' && <span className="badge badge-info">{viewModal.item.regulatory_class}</span>}
                </div>
              </div>
            </div>
            <div className="detail-grid" style={{ marginBottom: 14 }}>
              <div className="detail-item"><span className="detail-label">CAS Number</span><span className="detail-value" style={{ fontFamily: 'monospace' }}>{viewModal.item.cas_number || '—'}</span></div>
              <div className="detail-item"><span className="detail-label">Mol. Formula</span><span className="detail-value" style={{ fontFamily: 'monospace' }}>{viewModal.item.molecular_formula || '—'}</span></div>
              <div className="detail-item"><span className="detail-label">Mol. Weight</span><span className="detail-value">{viewModal.item.molecular_weight ? `${viewModal.item.molecular_weight} g/mol` : '—'}</span></div>
              <div className="detail-item"><span className="detail-label">Purity</span><span className="detail-value">{viewModal.item.purity || '—'}</span></div>
              <div className="detail-item"><span className="detail-label">Pack Sizes</span><span className="detail-value">{packSizes(viewModal.item)}</span></div>
              <div className="detail-item"><span className="detail-label">Price</span><span className="detail-value" style={{ fontWeight: 600 }}>{viewModal.item.price ? `${viewModal.item.currency === 'INR' ? '₹' : '$'}${Number(viewModal.item.price).toLocaleString()}` : '—'}</span></div>
              <div className="detail-item"><span className="detail-label">Storage</span><span className="detail-value">{viewModal.item.storage || '—'}</span></div>
              <div className="detail-item"><span className="detail-label">Lead Time</span><span className="detail-value">{viewModal.item.lead_time || '—'}</span></div>
              {viewModal.item.hsn_code && <div className="detail-item"><span className="detail-label">HSN Code</span><span className="detail-value" style={{ fontFamily: 'monospace' }}>{viewModal.item.hsn_code}</span></div>}
              {viewModal.item.parent_product && <div className="detail-item"><span className="detail-label">Parent Product</span><span className="detail-value">{viewModal.item.parent_product}</span></div>}
              <div className="detail-item"><span className="detail-label">Show on Website</span><span className="detail-value">{viewModal.item.show_on_website ? <span className="badge badge-success">Yes</span> : <span className="badge badge-neutral">No</span>}</span></div>
            </div>
            {viewModal.item.synonyms && <div style={{ marginBottom: 10, background: 'var(--surface-2)', padding: '8px 12px', borderRadius: 8, fontSize: '0.83rem', color: 'var(--text-secondary)' }}><span style={{ fontWeight: 600 }}>Synonyms: </span>{viewModal.item.synonyms}</div>}
            {viewModal.item.description && <div style={{ background: 'var(--surface-2)', padding: 12, borderRadius: 8, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{viewModal.item.description}</div>}
          </>
        )}
      </Modal>

      {/* Freeze Confirm */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Freeze Catalog Item"
        footer={<><button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button><button className="btn btn-danger" onClick={handleDelete}>Freeze</button></>}
      >
        <p>Are you sure you want to freeze <strong>{deleteConfirm?.name}</strong>? The record will be locked from editing. You can unfreeze it later.</p>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal open={bulkModal} onClose={() => { setBulkModal(false); setBulkResult(null); }} title="Bulk Upload Catalog Items" size="md"
        footer={
          bulkResult
            ? <button className="btn btn-secondary" onClick={() => { setBulkModal(false); setBulkResult(null); }}>Done</button>
            : <>
              <button className="btn btn-secondary" onClick={() => setBulkModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleBulkUpload} disabled={bulkUploading || !bulkFile}>
                {bulkUploading ? 'Uploading…' : 'Upload'}
              </button>
            </>
        }
      >
        {bulkResult ? (
          <div>
            <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
              <div style={{ fontWeight: 700, color: '#065f46' }}>Upload Complete</div>
              <div style={{ color: '#047857', fontSize: '0.875rem' }}>{bulkResult.inserted} items inserted successfully.</div>
              {bulkResult.skipped > 0 && <div style={{ color: '#b45309', fontSize: '0.875rem' }}>{bulkResult.skipped} items skipped.</div>}
            </div>
            {bulkResult.errors?.length > 0 && (
              <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px', fontSize: '0.8rem' }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Skipped items:</div>
                {bulkResult.errors.slice(0, 10).map((e, i) => (
                  <div key={i} style={{ color: '#92400e' }}>{e.name}: {e.error}</div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 16 }}>
              Upload a CSV or JSON file to add multiple catalog items at once. Maximum 500 items per upload.
            </p>
            <div style={{ marginBottom: 16 }}>
              <button className="btn btn-ghost btn-sm" onClick={downloadTemplate} style={{ fontSize: '0.8rem' }}>
                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                Download CSV Template
              </button>
            </div>
            <div>
              <label className="form-label">Select File (CSV or JSON)</label>
              <input type="file" accept=".csv,.json" className="form-control"
                onChange={e => { setBulkFile(e.target.files[0] || null); setBulkError(''); setBulkResult(null); }}
              />
            </div>
            {bulkFile && <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--text-muted)' }}>Selected: {bulkFile.name} ({(bulkFile.size / 1024).toFixed(1)} KB)</div>}
            {bulkError && <div className="alert alert-error" style={{ marginTop: 12 }}>{bulkError}</div>}
            <div style={{ marginTop: 16, background: '#f8fafc', borderRadius: 8, padding: '10px 14px', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>CSV columns:</div>
              <code style={{ fontSize: '0.72rem', wordBreak: 'break-all' }}>
                name, catalog_number, cas_number, molecular_formula, molecular_weight, category, pharmacopeia, purity, pack_sizes, price, currency, storage, lead_time, stock_status, hsn_code, synonyms, image_file_name, show_on_website, parent_product, description
              </code>
            </div>
          </div>
        )}
      </Modal>
      {/* Bulk Image Upload Modal */}
      <Modal open={imgBulkModal} onClose={() => { setImgBulkModal(false); setImgBulkResult(null); }} title="Bulk Upload Product Images" size="md"
        footer={
          imgBulkResult
            ? <button className="btn btn-secondary" onClick={() => { setImgBulkModal(false); setImgBulkResult(null); }}>Done</button>
            : <>
              <button className="btn btn-secondary" onClick={() => setImgBulkModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleBulkImageUpload} disabled={imgBulkUploading || !imgBulkFiles || imgBulkFiles.length === 0}>
                {imgBulkUploading ? 'Uploading…' : `Upload${imgBulkFiles ? ` (${imgBulkFiles.length})` : ''}`}
              </button>
            </>
        }
      >
        {imgBulkResult ? (
          <div>
            <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
              <div style={{ fontWeight: 700, color: '#065f46' }}>Upload Complete</div>
              <div style={{ color: '#047857', fontSize: '0.875rem' }}>{imgBulkResult.matched} image{imgBulkResult.matched !== 1 ? 's' : ''} matched and linked.</div>
              {imgBulkResult.not_found_count > 0 && <div style={{ color: '#b45309', fontSize: '0.875rem' }}>{imgBulkResult.not_found_count} file{imgBulkResult.not_found_count !== 1 ? 's' : ''} had no matching catalog number.</div>}
            </div>
            {imgBulkResult.not_found?.length > 0 && (
              <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px', fontSize: '0.8rem' }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Unmatched files (no product with that catalog number):</div>
                {imgBulkResult.not_found.slice(0, 20).map((f, i) => <div key={i} style={{ color: '#92400e' }}>{f}</div>)}
              </div>
            )}
          </div>
        ) : (
          <div>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 16 }}>
              Select multiple image files at once. Each file must be named after the product's catalog number (e.g. <code>PL0719.png</code>). Up to 200 images per upload, 5 MB per file.
            </p>
            <div>
              <label className="form-label">Select Image Files (PNG, JPG, WEBP)</label>
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple className="form-control"
                onChange={e => { setImgBulkFiles(e.target.files); setImgBulkError(''); setImgBulkResult(null); }}
              />
            </div>
            {imgBulkFiles && imgBulkFiles.length > 0 && (
              <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--text-muted)' }}>{imgBulkFiles.length} file{imgBulkFiles.length !== 1 ? 's' : ''} selected</div>
            )}
            {imgBulkError && <div className="alert alert-error" style={{ marginTop: 12 }}>{imgBulkError}</div>}
            <div style={{ marginTop: 16, background: '#f8fafc', borderRadius: 8, padding: '10px 14px', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>How it works:</div>
              <ul style={{ margin: 0, paddingLeft: 16, lineHeight: 1.8 }}>
                <li>File name = catalog number + extension (e.g. <code>PL0719.png</code>)</li>
                <li>Each image is automatically linked to the matching product</li>
                <li>Existing images for matched products will be replaced</li>
                <li>Files with no matching catalog number are skipped</li>
              </ul>
            </div>
          </div>
        )}
      </Modal>

      {/* Profitability Modal */}
      <Modal open={profitModal} onClose={() => setProfitModal(false)} title="Product Profitability" size="lg"
        footer={<button className="btn btn-secondary" onClick={() => setProfitModal(false)}>Close</button>}
      >
        {profitLoading ? (
          <div className="loading-overlay"><div className="spinner" /></div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>SKU</th>
                  <th>Selling Price</th>
                  <th>Cost Price</th>
                  <th>Margin %</th>
                  <th>Units Sold</th>
                  <th>Total Revenue</th>
                  <th>Total Profit</th>
                </tr>
              </thead>
              <tbody>
                {profitData.length === 0 && (
                  <tr><td colSpan={8}><div className="empty-state"><p>No profitability data available.</p></div></td></tr>
                )}
                {profitData.map(p => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--text-muted)' }}>{p.sku || '—'}</td>
                    <td>{p.selling_price != null ? `₹${Number(p.selling_price).toLocaleString()}` : '—'}</td>
                    <td>{p.cost_price != null ? `₹${Number(p.cost_price).toLocaleString()}` : '—'}</td>
                    <td>
                      {p.cost_price != null && p.selling_price != null ? (
                        <span style={{ color: Number(p.margin_pct) >= 20 ? '#15803d' : Number(p.margin_pct) >= 0 ? '#b45309' : '#dc2626', fontWeight: 600 }}>
                          {Number(p.margin_pct).toFixed(1)}%
                        </span>
                      ) : '—'}
                    </td>
                    <td>{Number(p.units_sold).toLocaleString()}</td>
                    <td>₹{Number(p.revenue).toLocaleString()}</td>
                    <td style={{ fontWeight: 600, color: Number(p.total_profit) >= 0 ? '#15803d' : '#dc2626' }}>
                      ₹{Number(p.total_profit).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
}
